/*
  Personalize panel on the product page. Elements share state per product form id:
    <mw-personalize>  the panel: photo (empty card / filled card with Change + remove), face check
    <mw-preview>      inside the panel: "Create Artwork", the preview result, hidden job/preview ids
  Add to Cart (Dawn's buy buttons, below Frame and Size) is always visible; when "require artwork"
  is on, submitting without artwork flags the photo card or the Create Artwork button instead.
  Tapping Create Artwork while the photo is still being checked waits for the check, then starts.

  Photo check (mw-photo-check.js, MediaPipe in the browser, CLAUDE.md rule 8): the photo is resized
  to ≤ 2048px JPEG and its faces counted the moment it is chosen. One message at most:
    no face → blocked · several faces → the parent taps their child. If MediaPipe cannot load,
    the photo is not blocked.

  Adapter contract:
    adapter.start({ photo, variantId, productHandle }, emit) → Promise<void>
      photo = { blob, faceCount, faceBox }
      emit({ status: 'running', message })
      emit({ status: 'done', jobId, previewId, imageUrl })
      emit({ status: 'failed', message })
  The real adapter talks to the app through the store's app proxy (data-api, default /apps/mw):
  the backend URL never reaches the browser. data-demo="true" swaps in a fake one.
*/
(() => {
  if (customElements.get('mw-preview')) return;

  // one shared state object per product form id
  const states = {};
  // checking: a Promise while a chosen photo is still being read and checked, else null
  const stateFor = (formId) => (states[formId] ||= { photo: null, checking: null, listeners: new Set() });
  const notify = (formId) => stateFor(formId).listeners.forEach((fn) => fn());

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollTo = (el) => el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  const COPY = {
    no_face_detected: "We couldn't find a face in this photo. Please try a different photo.",
    content_blocked: "We couldn't create artwork from this photo. Please try a different photo.",
    ai_unavailable: 'Our artists are very busy right now. Please try again in a few minutes.',
    unauthorized: 'Your session has expired. Please try again.',
    network: 'Please check your connection and try again.',
    timeout: 'This is taking longer than usual. Please try again in a moment.',
    default: 'Something went wrong. Please try again.',
  };

  /* ---------------- adapters ---------------- */

  const demoAdapter = (demoImage) => ({
    start(request, emit) {
      emit({ status: 'running', message: 'Creating your artwork…' });
      return wait(2500).then(() => {
        const id = `demo-${Date.now().toString(36)}`;
        emit({ status: 'done', jobId: id, previewId: id, imageUrl: demoImage, demo: true });
      });
    },
  });

  class ApiError extends Error {
    constructor(code, message) {
      super(message || COPY[code] || COPY.default);
      this.code = code;
    }
  }

  function appAdapter(api, ipPassUrl) {
    const base = api.replace(/\/$/, '');
    const jobKey = (handle) => `mw:job:${handle}`;
    let uploaded = null; // { blob, jobId }

    async function call(path, { method = 'GET', token, body, type } = {}) {
      const url = `${base}${path}${token ? `${path.includes('?') ? '&' : '?'}t=${encodeURIComponent(token)}` : ''}`;
      let res;
      try {
        res = await fetch(url, {
          method,
          headers: body ? { 'content-type': type || 'application/json' } : undefined,
          body: body && !type ? JSON.stringify(body) : body,
          credentials: 'same-origin',
        });
      } catch (err) {
        throw new ApiError('network');
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new ApiError(data.error || 'default', data.message);
      return data;
    }

    function readJob(handle) {
      try {
        return JSON.parse(localStorage.getItem(jobKey(handle)) || 'null');
      } catch (e) {
        return null;
      }
    }

    function saveJob(handle, job) {
      try {
        localStorage.setItem(jobKey(handle), JSON.stringify(job));
      } catch (e) {
        /* private mode: the job lives for this page only */
      }
    }

    async function ensureJob(request) {
      const saved = readJob(request.productHandle);
      if (saved && saved.jobId && saved.token) return saved;
      const job = await call('/jobs', {
        method: 'POST',
        body: { productHandle: request.productHandle, variantId: request.variantId || undefined },
      });
      saveJob(request.productHandle, job);
      return job;
    }

    async function upload(job, blob) {
      if (uploaded && uploaded.blob === blob && uploaded.jobId === job.jobId) return;
      await call(`/jobs/${job.jobId}/photo`, { method: 'POST', token: job.token, body: blob, type: 'image/jpeg' });
      uploaded = { blob, jobId: job.jobId };
    }

    // from the app directly, so it sees the shopper's real IP; never holds up or fails a preview
    async function ipPass(jobId) {
      try {
        const res = await fetch(`${ipPassUrl}?job=${encodeURIComponent(jobId)}`, {
          signal: AbortSignal.timeout(3000),
        });
        return res.ok ? (await res.json()).pass : null;
      } catch (err) {
        return null;
      }
    }

    async function start(request, emit) {
      emit({ status: 'running', message: 'Uploading your photo…' });
      let job = await ensureJob(request);
      let pass = ipPass(job.jobId);
      try {
        await upload(job, request.photo.blob);
      } catch (err) {
        // a job from an old visit that the server no longer knows: start a fresh one once
        if (err.code !== 'unauthorized') throw err;
        try {
          localStorage.removeItem(jobKey(request.productHandle));
        } catch (e) {
          /* ignore */
        }
        uploaded = null;
        job = await ensureJob(request);
        pass = ipPass(job.jobId);
        await upload(job, request.photo.blob);
      }

      emit({ status: 'running', message: 'Creating your artwork…' });
      const { previewId } = await call(`/jobs/${job.jobId}/previews`, {
        method: 'POST',
        token: job.token,
        body: {
          productHandle: request.productHandle,
          variantId: request.variantId || undefined,
          faceCount: request.photo.faceCount,
          faceBox: request.photo.faceBox,
          ipPass: await pass,
        },
      });

      // poll every 1.5 s for up to 3 minutes
      const deadline = Date.now() + 3 * 60 * 1000;
      while (Date.now() < deadline) {
        const view = await call(`/previews/${previewId}`, { token: job.token });
        if (view.status === 'done' && view.image) {
          emit({
            status: 'done',
            jobId: job.jobId,
            previewId,
            imageUrl: `${base}/previews/${previewId}/image?t=${encodeURIComponent(job.token)}`,
          });
          return;
        }
        if (view.status === 'failed') throw new ApiError(view.errorCode || 'ai_unavailable');
        if (view.status === 'finishing') emit({ status: 'running', message: 'Adding the finishing touches…' });
        await wait(1500);
      }
      throw new ApiError('timeout');
    }

    return { start };
  }

  /* ---------------- the panel: photo, face check ---------------- */

  class MwPersonalize extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId;
      this.state = stateFor(this.formId);
      this.input = this.querySelector('[data-photo]');
      this.input.addEventListener('change', (e) => this.onPhoto(e.target.files[0]));
      this.querySelector('[data-change]').addEventListener('click', () => this.choosePhoto());
      this.querySelector('[data-remove]').addEventListener('click', () => this.clearPhoto());
      this.checkRun = 0;
      this.warmPhotoCheck();
    }

    /**
     * Loads the face check (about 4.5 MB) before a photo is chosen, without slowing the page:
     * once the page has loaded and the browser is idle with this panel near the screen, or at the
     * latest when the parent taps the photo card. Data saver and 2G wait for the tap. Nothing is
     * uploaded here: the photo goes to the server only on Create Artwork.
     */
    warmPhotoCheck() {
      const warm = () => {
        const check = window.MadeOfWonderPhotoCheck;
        if (check && check.warm) check.warm();
      };
      const card = this.querySelector('[data-drop]');
      card.addEventListener('pointerdown', warm, { once: true });
      card.addEventListener('focusin', warm, { once: true });

      const conn = navigator.connection;
      if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;
      if (!('IntersectionObserver' in window)) return;
      const whenIdle = (fn) =>
        window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 1000);
      const start = () => {
        const seen = new IntersectionObserver(
          (entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            seen.disconnect();
            whenIdle(warm);
          },
          { rootMargin: '100% 0px' },
        );
        seen.observe(this);
      };
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
    }

    async onPhoto(file) {
      if (!file) return;
      if (!/^image\//.test(file.type) && !/\.(heic|heif)$/i.test(file.name)) {
        this.message('That file is not a photo. Please choose a JPG or PNG.', 'error');
        return;
      }
      const run = ++this.checkRun;
      let done;
      this.state.checking = new Promise((r) => (done = r));
      const finish = () => {
        if (run === this.checkRun) this.state.checking = null;
        done();
      };
      this.state.photo = null;
      this.invalid(false);
      this.hideFaces();
      this.setThumb(URL.createObjectURL(file));
      this.showFilled(true);
      this.setTip('Checking your photo…');
      this.message('');
      notify(this.formId);

      let result;
      try {
        if (typeof window.MadeOfWonderPhotoCheck !== 'function') throw new Error('photo check missing');
        result = await window.MadeOfWonderPhotoCheck(file);
      } catch (err) {
        if (run !== this.checkRun) return done();
        console.warn('[madeofwonder] photo could not be read', err);
        this.clearPhoto();
        this.message("We couldn't open this photo. Please try a different one.", 'error');
        return finish();
      }
      if (run !== this.checkRun) return done(); // another photo was chosen meanwhile
      try {
        this.judge(result);
      } finally {
        finish();
      }
    }

    /** Decides what to do with a checked photo: block it, ask which face, or accept it. */
    judge(result) {
      const faces = result.faces;
      const photo = { blob: result.blob, faceCount: faces ? faces.length : null, faceBox: null };
      this.setTip(this.dataset.tip);

      if (faces && faces.length === 0) {
        this.invalid(true, COPY.no_face_detected);
        this.message(COPY.no_face_detected, 'error');
        return;
      }
      if (faces && faces.length > 1) {
        this.chooseFace(result.canvas, faces, photo);
        return;
      }
      this.accept(photo);
    }

    accept(photo) {
      this.state.photo = photo;
      notify(this.formId);
    }

    chooseFace(canvas, faces, photo) {
      const box = this.querySelector('[data-faces]');
      const list = box.querySelector('[data-faces-list]');
      list.innerHTML = '';
      faces.slice(0, 8).forEach((face, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'mw-faces__pick';
        b.setAttribute('aria-label', `Face ${i + 1}`);
        const img = document.createElement('img');
        img.src = window.MadeOfWonderPhotoCheck.faceThumb(canvas, face, 96);
        img.alt = '';
        img.width = 64;
        img.height = 64;
        b.appendChild(img);
        b.addEventListener('click', () => {
          list.querySelectorAll('.mw-faces__pick').forEach((x) => x.setAttribute('aria-pressed', 'false'));
          b.setAttribute('aria-pressed', 'true');
          this.message('');
          this.accept({ ...photo, faceBox: { x: face.x, y: face.y, w: face.w, h: face.h } });
        });
        b.setAttribute('aria-pressed', 'false');
        list.appendChild(b);
      });
      box.hidden = false;
    }

    hideFaces() {
      const box = this.querySelector('[data-faces]');
      box.hidden = true;
      box.querySelector('[data-faces-list]').innerHTML = '';
    }

    /** Opens the file picker; cleared first so picking the same file again still fires `change`. */
    choosePhoto() {
      this.input.value = '';
      this.input.click();
    }

    clearPhoto() {
      this.checkRun++;
      this.state.photo = null;
      this.state.checking = null;
      this.input.value = '';
      this.setThumb(null);
      this.hideFaces();
      this.message('');
      this.showFilled(false);
      notify(this.formId);
    }

    setThumb(src) {
      const thumb = this.querySelector('[data-thumb]');
      if (thumb.src.startsWith('blob:')) URL.revokeObjectURL(thumb.src);
      if (src) thumb.src = src;
      else thumb.removeAttribute('src');
    }

    setTip(text) {
      this.querySelector('[data-tip]').textContent = text || '';
    }

    showFilled(filled) {
      this.querySelector('[data-drop]').hidden = filled;
      this.querySelector('[data-filled]').hidden = !filled;
    }

    /** One message under the photo at a time: 'error' (red) or 'tip' (muted). */
    message(text, kind) {
      const el = this.querySelector('[data-photo-msg]');
      el.textContent = text || '';
      el.dataset.kind = kind || '';
      el.hidden = !text;
    }

    // red outline on the photo card: no photo yet, or a photo that cannot be used
    invalid(on, text) {
      const filled = !this.querySelector('[data-filled]').hidden;
      this.querySelector(filled ? '[data-filled]' : '[data-drop]').classList.toggle('is-invalid', on);
      if (!on) this.querySelectorAll('.is-invalid').forEach((el) => el.classList.remove('is-invalid'));
      this.querySelector('[data-photo-error]').textContent = on ? text || 'Please add a photo first.' : '';
    }

    /** Why "Create Artwork" cannot run yet, or null. */
    blocker() {
      if (this.state.photo) return null;
      if (!this.querySelector('[data-faces]').hidden) return 'Please tap the face of your child.';
      return 'Please add a photo first.';
    }
  }

  /* ---------------- Create Artwork + result ---------------- */

  class MwPreview extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId;
      this.state = stateFor(this.formId);
      this.form = document.getElementById(this.formId);
      this.ready = false;
      this.$ = (sel) => this.querySelector(sel);

      this.onState = () => {
        if (!this.running) this.resetPreview();
        this.showError('');
        this.sync();
      };
      this.state.listeners.add(this.onState);
      this.$('[data-go]').addEventListener('click', () => this.create());
      // "Try a different photo": same as picking a new photo; the new photo resets the preview
      this.$('[data-again]').addEventListener('click', () => {
        const panel = this.panel();
        if (panel) panel.choosePhoto();
      });

      if (this.dataset.requirePreview === 'true') {
        // Capture phase runs before Dawn's product-form submit handler.
        this.onSubmit = (e) => {
          if (e.target !== this.form || this.ready) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          if (!this.state.photo && this.state.checking) {
            this.showError('Please wait a moment while we check your photo, then create your artwork.');
          } else if (!this.state.photo) {
            this.askForPhoto();
          } else {
            this.showError('Please create your artwork first.');
            scrollTo(this.$('[data-go]'));
          }
        };
        document.addEventListener('submit', this.onSubmit, true);
      }
      this.sync();
    }

    disconnectedCallback() {
      this.stopWait();
      this.state.listeners.delete(this.onState);
      if (this.onSubmit) document.removeEventListener('submit', this.onSubmit, true);
    }

    panel() {
      return document.querySelector(`mw-personalize[data-form-id="${this.formId}"]`);
    }

    adapter() {
      if (this.dataset.demo === 'true') return demoAdapter(this.dataset.demoImage);
      if (window.MadeOfWonderPreview && window.MadeOfWonderPreview.adapter) return window.MadeOfWonderPreview.adapter;
      return (this.api ||= appAdapter(this.dataset.api || '/apps/mw', this.dataset.ipPass));
    }

    sync() {
      this.$('[data-go]').disabled = Boolean(this.running);
    }

    askForPhoto() {
      const panel = this.panel();
      if (!panel) return;
      const why = panel.blocker() || 'Please add a photo first.';
      panel.invalid(true, why);
      panel.message(why, 'error');
      scrollTo(panel);
    }

    async create() {
      if (this.running || this.waiting) return;
      if (!this.state.photo && this.state.checking) {
        // tapped while the photo is still being read: wait for it instead of asking for a photo
        const go = this.$('[data-go]');
        const label = go.textContent;
        this.waiting = true;
        go.setAttribute('aria-busy', 'true');
        go.textContent = 'Checking your photo…';
        try {
          while (this.state.checking) await this.state.checking;
        } finally {
          this.waiting = false;
          go.removeAttribute('aria-busy');
          go.textContent = label;
        }
      }
      if (!this.state.photo) {
        this.askForPhoto();
        return;
      }
      this.preview();
    }

    /* waiting screen: the parent's photo, a progress bar with a countdown, rotating reviews */
    startWait() {
      this.stopWait();
      const wait = this.$('[data-wait]');
      const expected = Math.max(5, Number(wait.dataset.waitSeconds) || 30);
      const photo = this.$('[data-wait-photo]');
      const thumb = document.querySelector(`mw-personalize[data-form-id="${this.formId}"] [data-thumb]`);
      if (photo && thumb && thumb.src) photo.src = thumb.src;
      const started = Date.now();
      const tick = () => {
        const elapsed = (Date.now() - started) / 1000;
        // eases towards 95%; about 90% at the expected time, never 100% until the artwork arrives
        this.setProgress(Math.min(95, 100 * (1 - Math.exp((-2.3 * elapsed) / expected))));
        const left = Math.ceil(expected - elapsed);
        this.$('[data-wait-time]').textContent =
          left > 1 ? `About ${left} seconds left` : 'Almost done, adding the finishing touches…';
      };
      tick();
      this.waitTimer = setInterval(tick, 500);

      const slides = [...this.querySelectorAll('[data-slide]')];
      const dots = [...this.querySelectorAll('[data-dot]')];
      const show = (i) => {
        this.slide = (i + slides.length) % slides.length;
        slides.forEach((s, k) => (s.hidden = k !== this.slide));
        dots.forEach((d, k) => d.setAttribute('aria-current', String(k === this.slide)));
      };
      if (slides.length > 1) {
        show(0);
        dots.forEach((d, k) =>
          (d.onclick = () => {
            show(k);
            clearInterval(this.slideTimer);
            this.slideTimer = setInterval(() => show(this.slide + 1), 5000);
          })
        );
        this.slideTimer = setInterval(() => show(this.slide + 1), 5000);
      }
    }

    stopWait() {
      clearInterval(this.waitTimer);
      clearInterval(this.slideTimer);
    }

    setProgress(pct) {
      const value = Math.round(pct);
      this.$('[data-wait-fill]').style.width = `${value}%`;
      this.$('[data-wait-pct]').textContent = `${value}%`;
      this.$('[data-wait-bar]').setAttribute('aria-valuenow', String(value));
    }

    resetPreview() {
      this.ready = false;
      this.$('[data-preview]').hidden = true;
      this.$('[data-go]').hidden = false;
      this.$('[data-preview-id]').value = '';
      this.$('[data-job-id]').value = '';
    }

    async preview() {
      this.showError('');
      this.resetPreview();
      this.running = true;
      this.sync();
      this.$('[data-go]').hidden = true; // only the waiting screen while the artwork is made
      this.$('[data-again]').hidden = true;
      this.$('[data-preview]').hidden = false;
      this.$('[data-preview-img]').hidden = true;
      this.$('[data-frame]').hidden = true;
      this.$('[data-wait]').hidden = false;
      this.startWait();

      const variantInput = this.form && this.form.querySelector('[name="id"]');
      try {
        await this.adapter().start(
          {
            photo: this.state.photo,
            variantId: variantInput ? variantInput.value : null,
            productHandle: this.dataset.productHandle,
          },
          (event) => this.onEvent(event),
        );
      } catch (err) {
        console.warn('[madeofwonder] artwork failed', err);
        this.onEvent({ status: 'failed', message: err instanceof ApiError ? err.message : COPY.default });
      } finally {
        this.running = false;
        this.sync();
      }
    }

    onEvent(event) {
      if (event.status === 'running') {
        this.$('[data-wait-text]').textContent = event.message || 'Creating your artwork…';
      } else if (event.status === 'done') {
        const img = this.$('[data-preview-img]');
        img.onload = () => {
          // a beat at 100% before the artwork replaces the waiting screen
          this.stopWait();
          this.setProgress(100);
          this.$('[data-wait-time]').textContent = 'Done';
          setTimeout(() => {
            img.hidden = false;
            this.$('[data-frame]').hidden = false;
            this.$('[data-wait]').hidden = true;
          }, 400);
        };
        img.src = event.imageUrl;
        this.$('[data-job-id]').value = event.jobId;
        this.$('[data-preview-id]').value = event.previewId;
        const note = this.$('[data-note]');
        note.hidden = !event.demo;
        note.textContent = event.demo ? 'Demo preview: the real artwork arrives once the app is connected.' : '';
        this.ready = true;
        this.$('[data-go]').hidden = true;
        this.$('[data-again]').hidden = false;
      } else if (event.status === 'failed') {
        this.stopWait();
        this.$('[data-preview]').hidden = true;
        this.$('[data-go]').hidden = false;
        this.showError(event.message || COPY.default);
      }
    }

    showError(message) {
      const el = this.$('[data-error]');
      el.textContent = message;
      el.hidden = !message;
    }
  }

  // guide modals: any [data-mw-guide="frame|size|photo"] opens the matching <dialog>
  if (!window.mwGuidesReady) {
    window.mwGuidesReady = true;
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-mw-guide]');
      if (opener) {
        const dialog = document.querySelector(`[data-mw-guide-dialog="${opener.dataset.mwGuide}"]`);
        if (dialog && typeof dialog.showModal === 'function') {
          e.preventDefault();
          dialog.showModal();
        }
        return;
      }
      if (e.target.closest('[data-mw-guide-close]')) {
        e.target.closest('dialog').close();
        return;
      }
      // click on the backdrop closes
      if (e.target.matches('dialog.mw-guide')) e.target.close();
    });
  }

  customElements.define('mw-personalize', MwPersonalize);
  customElements.define('mw-preview', MwPreview);
})();
